import type { PendingSubmission, SubmissionStore } from '@p4p2r0/minesweeper-core';

/**
 * Local identity storage.
 *
 * The token is a login secret, so it stays in this browser and travels only in
 * an `Authorization` header — never in a URL or a cookie.
 */
export interface Identity {
  token: string;
  username: string;
  playerId: string;
}

const STORAGE_KEY = 'minesweeper.identity.v1';

export function apiBaseUrl(): string {
  const configured = import.meta.env.VITE_API_URL as string | undefined;
  return (configured ?? 'https://minesweeper-api.workers.dev').replace(/\/+$/, '');
}

export function loadIdentity(): Identity | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Identity>;
    if (!parsed.token || !parsed.username || !parsed.playerId) return null;
    return { token: parsed.token, username: parsed.username, playerId: parsed.playerId };
  } catch {
    return null;
  }
}

export function saveIdentity(identity: Identity | null): void {
  try {
    if (identity === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(identity));
  } catch {
    // Private browsing modes can refuse writes; play on regardless.
  }
}

/**
 * Durable parking for finished games that have not reached the server yet.
 *
 * A win earned on a patchy connection should survive a reload, a closed tab,
 * or a phone that went to sleep mid-flight — the queue is the record that the
 * game happened, and the network is only how it eventually gets there. The
 * session drains this on its own once a connection is available; see
 * `SubmissionStore` in the core package for the contract.
 */
const PENDING_KEY = 'minesweeper.pending.v1';

export function createSubmissionStore(): SubmissionStore {
  return {
    read(): PendingSubmission[] {
      try {
        const raw = window.localStorage.getItem(PENDING_KEY);
        if (!raw) return [];
        const parsed: unknown = JSON.parse(raw);
        return Array.isArray(parsed) ? (parsed as PendingSubmission[]) : [];
      } catch {
        // Unreadable or corrupt: an empty queue is the safe reading, and the
        // next write replaces whatever was there.
        return [];
      }
    },
    write(pending: PendingSubmission[]): void {
      try {
        if (pending.length === 0) window.localStorage.removeItem(PENDING_KEY);
        else window.localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
      } catch {
        // A full or unavailable store must not cost anyone their game; the
        // session keeps the queue in memory regardless.
      }
    },
  };
}
